/**
 * The search params of /stock/lots and /stock/lots/$lotNo (§0, §4.8).
 *
 * Apart from the pages for the same reason W-06's and W-07's parsers are:
 * `validateSearch` runs in the route module the router loads eagerly, so a parser
 * that imported the page would drag the graph, the panel and the maintenance
 * drawer into the initial bundle.
 */
import { LOT_FILTERS, TRACE_DIRECTIONS, type LotFilter, type TraceDirection } from '@aslen/core';

export interface LotsSearch {
  /** Free text: lot no, SKU, item name, supplier batch, container number. */
  q?: string;
  /** The state chip; "all" is the default and stays out of the URL. */
  f?: LotFilter;
  /** csv of `lots.series` values */
  series?: string;
  /** csv of supplier contact ids */
  supplier?: string;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined);

export function parseLotsSearch(s: Record<string, unknown>): LotsSearch {
  const f = (LOT_FILTERS as readonly string[]).includes(s.f as string) ? (s.f as LotFilter) : undefined;
  return { q: str(s.q), f: f === 'all' ? undefined : f, series: str(s.series), supplier: str(s.supplier) };
}

export interface TraceSearch {
  /** The selected node's key (`lot:<uuid>`, `sale:<uuid>`, …). */
  node?: string;
  /** Which way the chain is read; "both" is the default and stays out of the URL. */
  dir?: TraceDirection;
  /** `?edit=1` opens the lot-maintenance drawer. */
  edit?: boolean;
}

export function parseTraceSearch(s: Record<string, unknown>): TraceSearch {
  const dir = (TRACE_DIRECTIONS as readonly string[]).includes(s.dir as string) ? (s.dir as TraceDirection) : undefined;
  return {
    node: str(s.node),
    dir: dir === 'both' ? undefined : dir,
    // the router parses `?edit=1` into the number 1 and `?edit=true` into a boolean
    edit: s.edit === true || s.edit === 1 || s.edit === '1' || s.edit === 'true' ? true : undefined,
  };
}

export const splitCsv = (v: string | undefined): string[] => (v ? v.split(',').filter(Boolean) : []);
export const joinCsv = (v: string[]): string | undefined => (v.length ? v.join(',') : undefined);
